import { usePlatform } from "components/utils/PlatformContext";
import { Link } from "react-router-dom";

interface MainWindowProps {
  children?: React.ReactNode;
}

const MainWindow: React.FC<MainWindowProps> = ({ children }) => {
  // Get the platform context
  const {
    title,
    landingBlurb,
    logo,
    privacyPolicyUrl,
    termsOfUseUrl,
    supportUrl,
    st4sBadge,
    poweredByEdtech,
  } = usePlatform();

  // Render the edtech logo just if the current logo exists
  const edtechLogo = logo ? "/EdtechLogo.png" : undefined;
  const st4sBadgeLogo = logo ? "/ST4S_Badge_2024_Colour.png" : undefined;

  return (
    <div className="w-full h-full min-h-screen inline-flex items-center justify-between p-14 z-10 overflow-auto">
      <div className="w-fit" />
      <div className="min-w-fit w-full h-full max-w-[1276px] p-[128px] bg-neutral-50 bg-opacity-70 rounded-2xl shadow backdrop-blur-[32px] justify-start items-center gap-24 inline-flex ">
        <div className="min-w-[400px] max-w-[414px] w-full self-stretch flex-col justify-start items-start gap-14 inline-flex">
          <div className="inline-flex flex-col justify-start gap-10">
            <div className="w-fit justify-start items-start inline-flex gap-6">
              <Link to="/">
                <img
                  className="w-auto h-[101px] select-none"
                  src={logo}
                  alt="Platform logo"
                />
              </Link>
              {poweredByEdtech && (
                <>
                  <div className="w-px self-stretch bg-neutral-500" />
                  <Link
                    className="h-[101px] inline-flex flex-col items-start justify-between"
                    to="https://www.edtechgroup.com.au/"
                    target="_blank"
                  >
                    <div className="text-start justify-start h-fit text-neutral-700 text-xs font-bold font-sans select-none">
                      Powered by
                    </div>
                    <img
                      className="w-auto h-[65px] select-none"
                      src={edtechLogo}
                      alt="Powered by logo"
                    />
                  </Link>
                </>
              )}
            </div>
            <div className="self-stretch inline-flex flex-col gap-4">
              <div className="self-stretch text-neutral-700 text-base font-normal font-sans leading-7">
                {landingBlurb}
              </div>
              {/* Show the links just if there is no ST4S badge here */}
              {!st4sBadge && (
                <div className="self-stretch inline-flex gap-10 items-center justify-center">
                  {supportUrl && (
                    <Link
                      to={supportUrl}
                      target="_blank"
                      className="self-stretch text-neutral-700 text-base font-semibold font-sans leading-7 underline hover:text-neutral-900"
                    >
                      Support
                    </Link>
                  )}
                  {privacyPolicyUrl && (
                    <Link
                      to={privacyPolicyUrl}
                      target="_blank"
                      className="self-stretch text-neutral-700 text-base font-semibold font-sans leading-7 underline hover:text-neutral-900"
                    >
                      Privacy policy
                    </Link>
                  )}
                  {termsOfUseUrl && (
                    <Link
                      to={termsOfUseUrl}
                      target="_blank"
                      className="self-stretch text-neutral-700 text-base font-semibold font-sans leading-7 underline hover:text-neutral-900"
                    >
                      Terms of use
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
          {/* ST4S badge image */}
          {st4sBadge && (
            <div className="inline-flex justify-start items-center gap-6">
              <Link to="https://st4s.edu.au/badge-program/" target="_blank">
                <img
                  className="min-w-[81px] w-[81px] h-24 select-none"
                  alt="ST4S-badge"
                  src={st4sBadgeLogo}
                />
              </Link>
              <div className="self-stretch inline-flex items-center">
                <div className="text-neutral-700 text-sm font-semibold font-sans leading-6 select-none">
                  {title} has successfully qualified to be part of the Safer
                  Technologies 4 Schools Badge Program.
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="w-px self-stretch bg-white" />
        <div className="min-w-[300px] max-w-[414px] w-full self-stretch flex-col justify-between items-center inline-flex">
          {children}
        </div>
      </div>
      <div className="w-fit" />
    </div>
  );
};

export default MainWindow;
